import React from 'react'
import { useEffect, useState, useMemo } from "react";
import styled from "styled-components";
import { Splide, SplideSlide } from '@splidejs/react-splide';
import '@splidejs/splide/dist/css/splide.min.css';
import { Link } from 'react-router-dom';
import { FiClock } from 'react-icons/fi';
import ShimmerCard from "./ShimmerCard";
import cacheManager from '../utils/cacheManager';
import { filterRecipesWithImages } from '../utils/recipeUtils';

// Recipe slide that hides itself entirely if image fails to load
function RecipeSlide({ recipe }) {
  const [imageError, setImageError] = useState(false);

  if (imageError) return null;

  return (
    <SplideSlide className='veggieSlide'>
      <Card className='veggieCard'>
        <CardLink to={'/recipe/' + recipe.id}>
          <ImageWrapper>
            <img
              src={recipe.image}
              alt={recipe.title}
              onError={() => setImageError(true)}
            />
            <Gradient />
          </ImageWrapper>
          <CardContent>
            <CardTitle>{recipe.title}</CardTitle>
            <CardMeta>
              {recipe.readyInMinutes > 0 && (
                <MetaBadge>
                  <MetaIcon><FiClock /></MetaIcon>
                  {recipe.readyInMinutes}m
                </MetaBadge>
              )}
              {recipe.spoonacularScore != null && (
                <MetaBadge>
                  <MetaIcon>⭐</MetaIcon>
                  {Math.round(recipe.spoonacularScore)}
                </MetaBadge>
              )}
            </CardMeta>
          </CardContent>
        </CardLink>
      </Card>
    </SplideSlide>
  );
}

// Helper to get Thanksgiving date (4th Thursday of November)
const getThanksgivingDate = (year) => {
  const november = new Date(year, 10, 1); // November 1st
  const dayOfWeek = november.getDay();
  const firstThursday = dayOfWeek <= 4 ? 5 - dayOfWeek : 12 - dayOfWeek;
  return new Date(year, 10, firstThursday + 21); // 4th Thursday
};

// Check if current date is within a holiday week
const getHolidayMode = () => {
  const now = new Date();
  const year = now.getFullYear();

  // Thanksgiving week: 3 days before to 3 days after Thanksgiving
  const thanksgiving = getThanksgivingDate(year);
  const thanksgivingStart = new Date(thanksgiving);
  thanksgivingStart.setDate(thanksgiving.getDate() - 3);
  const thanksgivingEnd = new Date(thanksgiving);
  thanksgivingEnd.setDate(thanksgiving.getDate() + 3);

  if (now >= thanksgivingStart && now <= thanksgivingEnd) {
    return {
      mode: 'thanksgiving',
      title: 'Thanksgiving Picks',
      tags: ['thanksgiving', 'turkey', 'fall'],
      cacheKey: 'thanksgiving',
      minScore: 80
    };
  }

  // Christmas week: Dec 20 - Dec 26
  const christmasStart = new Date(year, 11, 20);
  const christmasEnd = new Date(year, 11, 26);

  if (now >= christmasStart && now <= christmasEnd) {
    return {
      mode: 'christmas',
      title: 'Christmas Picks',
      tags: ['christmas', 'holiday', 'winter'],
      cacheKey: 'christmas',
      minScore: 80
    };
  }

  // Default: Vegetarian
  return {
    mode: 'vegetarian',
    title: 'Vegetarian Picks',
    tags: ['vegetarian'],
    cacheKey: 'veggie',
    minScore: 70
  };
};

function Veggie() {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);

  const holiday = useMemo(() => getHolidayMode(), []);

  useEffect(() => {
    getRecipes();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holiday.mode]);

  const getRecipes = async () => {
    setLoading(true);

    // Check cache first
    const cached = await cacheManager.get(holiday.cacheKey, 'POPULAR');
    if (cached) {
      setRecipes(cached);
      setLoading(false);
      return;
    }

    // Fetch from API - handle multiple tags for holidays
    try {
      const tags = Array.isArray(holiday.tags) ? holiday.tags : [holiday.tags];
      const allRecipes = [];
      const seenIds = new Set();

      // Fetch from each tag (limit API calls for performance)
      for (const tag of tags.slice(0, 4)) {
        try {
          const api = await fetch(
            `https://api.spoonacular.com/recipes/random?apiKey=${process.env.REACT_APP_API_KEY}&number=50&tags=${tag}`
          );
          const data = await api.json();

          // Add unique recipes
          for (const recipe of (data.recipes || [])) {
            if (!seenIds.has(recipe.id)) {
              seenIds.add(recipe.id);
              allRecipes.push(recipe);
            }
          }

          // Stop if we have enough candidates
          if (allRecipes.length >= 100) break;
        } catch (tagError) {
          console.warn(`Error fetching ${tag} recipes:`, tagError);
        }
      }

      // Filter out recipes without valid images AND require minimum score
      const filteredRecipes = filterRecipesWithImages(allRecipes)
        .filter(recipe => recipe.spoonacularScore >= holiday.minScore)
        .slice(0, 21);

      // Cache the results (7 day TTL)
      await cacheManager.set(holiday.cacheKey, filteredRecipes, 'POPULAR');

      setRecipes(filteredRecipes);
    } catch (error) {
      console.error(`Error fetching ${holiday.mode} recipes:`, error);
      setRecipes([]);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <Wrapper className='veggieWrapper'>
        <SectionTitle>{holiday.title}</SectionTitle>
        <Splide
          options={{
            type: 'slide',
            perPage: 3,
            perMove: 3,
            arrows: true,
            pagination: false,
            drag: true,
            wheel: false,
            autoplay: !loading,
            interval: 4000,
            pauseOnHover: true,
            pauseOnFocus: true,
            resetProgress: false,
            gap: "1.5rem",
            speed: 600,
            easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
            breakpoints: {
              1300: {
                perPage: 2,
                perMove: 2,
              },
              900: {
                perPage: 2,
                perMove: 2,
                gap: "1rem",
                arrows: false,
              },
              600: {
                perPage: 1,
                perMove: 1,
                arrows: false,
              },
            },
          }}
        >
          {loading ? (
            Array.from({ length: 6 }).map((_, index) => (
              <SplideSlide className='veggieSlide' key={`shimmer-${index}`}>
                <ShimmerCard />
              </SplideSlide>
            ))
          ) : (
            recipes.map((recipe) => (
              <RecipeSlide key={recipe.id} recipe={recipe} />
            ))
          )}
        </Splide>
      </Wrapper>
    </div>
  )
}

const Wrapper = styled.div`
  margin: var(--spacing-lg) 0;
  position: relative;
  padding: 0 var(--spacing-sm);
  cursor: grab;
  overflow: hidden;
  width: 100%;

  &:active {
    cursor: grabbing;
  }

  /* Subtle fade hint for scrollability */
  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 40px;
    pointer-events: none;
    z-index: 2;
    transition: opacity var(--transition-base);
  }

  &::before {
    left: 0;
    background: linear-gradient(to right, var(--color-background), transparent);
    opacity: 0;
  }

  &::after {
    right: 0;
    background: linear-gradient(to left, var(--color-background), transparent);
  }

  @media screen and (max-width: 900px) {
    margin: var(--spacing-sm) 0;
    padding: 0;

    &::before,
    &::after {
      display: none;
    }
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: var(--font-weight-medium);
  color: var(--color-text-secondary);
  margin-bottom: var(--spacing-sm);
  text-align: left;
  letter-spacing: -0.01em;

  @media screen and (max-width: 900px) {
    font-size: 1.125rem;
    text-align: left;
    margin-bottom: var(--spacing-xs);
    padding-left: var(--spacing-xs);
  }
`;

const Card = styled.div`
  border-radius: var(--radius-md);
  overflow: hidden;
  position: relative;
  background: var(--color-surface-elevated);
  border: 1px solid var(--color-border-subtle);
  transition: transform var(--transition-fast), background var(--transition-fast), border-color var(--transition-fast), box-shadow var(--transition-fast);
  height: 100%;

  &:hover {
    transform: translateY(-3px);
    border-color: var(--color-border);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
  }
`;

const CardLink = styled(Link)`
  display: block;
  text-decoration: none;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  padding-top: 66.67%;
  overflow: hidden;
  background: var(--color-border);

  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const CardContent = styled.div`
  padding: var(--spacing-sm);
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-height: 70px;
`;

const CardTitle = styled.p`
  color: var(--color-text-primary);
  font-size: 0.9375rem;
  font-weight: var(--font-weight-medium);
  line-height: 1.4;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  letter-spacing: -0.01em;

  @media screen and (max-width: 900px) {
    font-size: 0.875rem;
  }
`;

const CardMeta = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: auto;
`;

const MetaBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.8125rem;
  color: var(--color-text-secondary);
  font-weight: var(--font-weight-normal);
`;

const MetaIcon = styled.span`
  font-size: 0.75rem;
  display: flex;
  align-items: center;
`;

const Gradient = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 40%;
  background: linear-gradient(to top, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0) 100%);
  z-index: 1;
  pointer-events: none;
`

export default Veggie
